import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import { SIMULATION_DOCUMENT_TYPES } from 'constants/digitalLoanPhase4';
import { formatDigitalLoanMoney } from '../utils/moneyInput';
import { formatPhase4DateTime } from '../utils/phase4Flow';

function termUnitFor(frequency, literals) {
  if (frequency === 'MENSUAL') return literals.termUnit || '';
  if (frequency === 'CATORCENAL') return literals.termUnitBiweekly || '';
  return '';
}

const FinalOfferScreen = ({
  literals,
  offer,
  notice = '',
  documentError = '',
  isLoading = false,
  isSubmitting = false,
  openingDocumentId = '',
  onOpenDocument = () => {},
  onAccept,
  onReject,
  onRetry,
}) => {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const frequency = offer?.paymentFrequency;
  const schedule = offer?.paymentSchedule || [];
  const termUnit = termUnitFor(frequency, literals);
  const canAccept = (
    offer
    && Number.isFinite(Number(offer.offerAmount))
    && Number.isFinite(Number(offer.offerTerm))
    && !isLoading
    && !isSubmitting
  );
  return (
    <div className='full-screen-container digital-loan-preoffer'>
      <div className='screen-content digital-loan-preoffer__scroll'>
        <h1 className='digital-loan-phase2__title digital-loan-preoffer__title'>
          {literals.title}
        </h1>
        <p className='digital-loan-phase2__body mb-4'>{literals.subtitle}</p>

        {offer?.currentStep === 'BACKOFFICE_REVIEW' && (
          <p className='digital-loan-phase2__body mb-3' role='status'>
            {literals.backofficeReview}
          </p>
        )}

        {notice && (
          <p className='digital-loan-phase2__body mb-3' role='status'>
            {notice}
          </p>
        )}

        {offer?.operationalWindowOk === false && (
          <p className='digital-loan-phase2__body mb-3' role='status'>
            {literals.windowWarning}
            {offer.pendingUntil
              ? ` ${formatPhase4DateTime(offer.pendingUntil)}`
              : ''}
          </p>
        )}

        {isLoading && (
          <p className='digital-loan-phase2__body' role='status'>
            {literals.loading}
          </p>
        )}

        {offer && !isLoading && (
          <div className='digital-loan-summary-block'>
            <div className='digital-loan-summary-block__row'>
              <span className='digital-loan-summary-block__label'>
                {literals.amountLabel}
              </span>
              <strong>{formatDigitalLoanMoney(offer.offerAmount, '- -')}</strong>
            </div>
            <div className='digital-loan-summary-block__row'>
              <span className='digital-loan-summary-block__label'>
                {literals.termLabel}
              </span>
              <strong>
                {offer.offerTerm ?? literals.emptyValue}
                {offer.offerTerm && termUnit ? ` ${termUnit}` : ''}
              </strong>
            </div>
            <div className='digital-loan-summary-block__row'>
              <span className='digital-loan-summary-block__label'>
                {literals.commissionLabel}
              </span>
              <strong>{formatDigitalLoanMoney(offer.commission, '- -')}</strong>
            </div>
            <div className='digital-loan-summary-block__row'>
              <span className='digital-loan-summary-block__label'>
                {literals.rateLabel}
              </span>
              <strong>
                {offer.interestRate ?? literals.emptyValue}
                {offer.interestRate != null ? '%' : ''}
              </strong>
            </div>
            <div className='digital-loan-summary-block__row'>
              <span className='digital-loan-summary-block__label'>
                {literals.amountWithCommissionLabel}
              </span>
              <strong>
                {formatDigitalLoanMoney(offer.amountWithCommission, '- -')}
              </strong>
            </div>
            <div className='digital-loan-summary-block__row'>
              <span className='digital-loan-summary-block__label'>
                {literals.paymentTypeLabel}
              </span>
              <strong>{offer.paymentType || literals.emptyValue}</strong>
            </div>
            <div className='digital-loan-summary-block__row'>
              <span className='digital-loan-summary-block__label'>
                {literals.totalLabel}
              </span>
              <strong>{formatDigitalLoanMoney(offer.totalToPay, '- -')}</strong>
            </div>
            {offer.offerExpiresAt && (
              <div className='digital-loan-summary-block__row'>
                <span className='digital-loan-summary-block__label'>
                  {literals.expiresLabel}
                </span>
                <strong>{formatPhase4DateTime(offer.offerExpiresAt)}</strong>
              </div>
            )}
          </div>
        )}

        {offer && !isLoading && (
          <div className='digital-loan-summary-block'>
            <Button
              text={literals.viewSchedule}
              type='link'
              onClick={() => setScheduleOpen((current) => !current)}
            />
            {scheduleOpen && schedule.length === 0 && (
              <p className='digital-loan-phase2__body mt-3'>
                {literals.emptySchedule}
              </p>
            )}
            {scheduleOpen && schedule.map((row) => (
              <div
                className='digital-loan-summary-block__row'
                key={`${row.position || ''}-${row.period || ''}`}
              >
                <span className='digital-loan-summary-block__label'>
                  {row.position || ''}
                  {' '}
                  {row.period || ''}
                </span>
                <strong>{formatDigitalLoanMoney(row.payment, '- -')}</strong>
              </div>
            ))}
            <h2 className='digital-loan-summary-block__title mt-4'>
              {literals.documentsTitle}
            </h2>
            {SIMULATION_DOCUMENT_TYPES.map((document) => (
              <div className='mb-2' key={document.id}>
                <Button
                  text={literals.documents?.[document.literalKey]}
                  type='link'
                  loading={openingDocumentId === document.id}
                  disabled={Boolean(openingDocumentId)}
                  onClick={() => onOpenDocument(document)}
                />
              </div>
            ))}
            {documentError && (
              <p className='digital-loan-phase2__body' role='alert'>
                {documentError}
              </p>
            )}
          </div>
        )}

        {!offer && !isLoading && (
          <Button text={literals.retry} type='secondary' onClick={onRetry} />
        )}
      </div>
      <div className='screen-actions'>
        <Button
          text={literals.accept}
          disabled={!canAccept}
          loading={isSubmitting}
          onClick={onAccept}
        />
        {onReject && (
          <Button
            text={literals.reject}
            type='secondary'
            disabled={isSubmitting || isLoading || !offer}
            onClick={onReject}
          />
        )}
      </div>
    </div>
  );
};

FinalOfferScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  offer: PropTypes.object,
  notice: PropTypes.string,
  documentError: PropTypes.string,
  isLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  openingDocumentId: PropTypes.string,
  onOpenDocument: PropTypes.func,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func,
  onRetry: PropTypes.func.isRequired,
};

export default FinalOfferScreen;
